import { useSyncedRef } from "@react-hookz/web";
import type { FunctionComponent } from "react";
import { useCallback } from "react";

import {
  useAdProfile,
  useAdUnitPath,
  useUserRoleTarget,
} from "~/components/advertisement/ad-slots/hooks";
import type { AdUnit, OutstreamAdUnit, Targeting } from "~/components/advertisement/ad-slots/types";
import * as aps from "~/components/advertisement/amazon-publisher-service/hooks";
import * as gpt from "~/components/advertisement/google-publisher-tag/hooks";
import * as ix from "~/components/advertisement/index-exchange-wrapper/hooks";
import { useTrackViewability } from "~/components/advertisement/viewability-tracking";
import { useIntersection } from "~/lib/hooks/intersection";

import { useAutoRefresh } from "./auto-refresh";

export type SlotProps = {
  adUnit: Exclude<AdUnit, OutstreamAdUnit>;
  autoRefreshOptions?: {
    intervalRefreshOptions?: {
      isDisable?: boolean;
    };
    scrollBackRefreshOptions?: {
      isDisable?: boolean;
      slotArticleEntityId?: string;
    };
  };
  className?: string;
  manualRefreshCount?: number;
  mutationObserveElement?: (element: HTMLElement | null) => void;
  sizes: googletag.GeneralSize;
  targeting?: Targeting;
  zone?: string | null;
};

export const Slot: FunctionComponent<SlotProps> = ({
  adUnit,
  autoRefreshOptions,
  className,
  manualRefreshCount = 0,
  mutationObserveElement,
  sizes,
  targeting = {},
  zone,
}) => {
  const adUnitPath = useAdUnitPath(adUnit, zone);
  const userRoleTarget = useUserRoleTarget();
  const { adProfile } = useAdProfile();
  const slot = gpt.useDefineSlot(adUnitPath, sizes, {
    ...targeting,
    ...userRoleTarget,
    adProfile,
    ...ix.useTargeting(adUnit),
  });

  const { captureIntersectionElement, element, intersection } = useIntersection({
    threshold: 0.45,
  });
  const elementReference = useSyncedRef(element);

  const { totalRefreshCount } = useAutoRefresh({
    adUnit,
    manualRefreshCount,
    options: {
      intervalRefreshOptions: {
        isDisable: autoRefreshOptions?.intervalRefreshOptions?.isDisable,
        slotIntersectionEntry: intersection,
        slotReference: elementReference,
      },
      scrollBackRefreshOptions: {
        isDisable: autoRefreshOptions?.scrollBackRefreshOptions?.isDisable,
        slotArticleEntityId: autoRefreshOptions?.scrollBackRefreshOptions?.slotArticleEntityId,
        slotIntersectionEntry: intersection,
      },
    },
    slot,
  });
  useTrackViewability(slot, intersection);

  aps.useRenderSlot(slot, totalRefreshCount);

  const handleCallbackReference = useCallback(
    (element: HTMLDivElement) => {
      captureIntersectionElement(element);
      mutationObserveElement?.(element);
    },
    [captureIntersectionElement, mutationObserveElement],
  );

  return <div className={className} id={slot?.getSlotElementId()} ref={handleCallbackReference} />;
};

Slot.displayName = "Slot";
